import { ExpressionType, IModel, IViolation, IAttribute, ModelConstants, ParameterConfig } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { VariablesConfig } from 'app/model/pipeline/variables-config';
import { ValueAdapter } from '../adapters/value.adapter';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Owned } from '../base/named';
import { convertStringToExpressionType } from '../expressions/expression-resolution';
import { format } from 'app/shared/utils/string.util';

@Metamodel.model({
    name: ModelConstants.setVariableActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.SetVariables),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.SetVariables)
    ],
    referable: true
})
export class SetVariableActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly VALUE: IAttribute;
    public static readonly SETSYSTEMVARIABLE: IAttribute;
    public static readonly PIPELINEOUTPUTS: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        display: stringResources.SetVariables,
        constraint: {
            validator: SetVariableActivity.validateVariables
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public variables: VariablesConfig;

    @Metamodel.attribute({
        templatable: true,
        display: stringResources.SetVariables,
        association: 'VALUE',
        dynamicAccepts: SetVariableActivity.getChosenVariableType
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public value: string | any[] | boolean;

    @Metamodel.attribute({
        association: 'SETSYSTEMVARIABLE',
        display: stringResources.SetVariables,
        templatable: true,
        constraint: {
            required: false,
        }
    })
    public setSystemVariable?: boolean;

    @Metamodel.attribute({
        association: 'PIPELINEOUTPUTS',
        display: stringResources.SetVariables,
        templatable: true,
        constraint: {
            required: false,
        }
    })
    public pipelineOutputs?: ParameterConfig[];

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static validateVariables(_variableActivity?: SetVariableActivity, url?: string): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (!_variableActivity.variables) {
            violations.push({
                message: () => stringResources.No_Variable_In_Pipeline
            });
        } else if (!_variableActivity.variables.name) {
            violations.push({
                message: () => stringResources.Set_Variable_Undefined
            });
        } else if (!_variableActivity.variables.value) {
            const expressionValue = ValueAdapter.toResource(_variableActivity, SetVariableActivity.VALUE);
            if (!expressionValue) {
                violations.push({
                    message: () => stringResources.Set_Variable_Value_Undefined
                });
            }
        } else {
            let pipeline: Owned = _variableActivity?.owner;
            while (pipeline?.owner) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pipeline = pipeline.owner as any;
            }

            if (pipeline instanceof Pipeline) {
                const pipelineVar = pipeline.variables.find((variable) =>
                    variable.name === _variableActivity.variables.name
                );
                if (!pipelineVar && !_variableActivity.setSystemVariable) {
                    violations.push({
                        message: () => stringResources.Variable_Does_Not_Exists
                    });
                }
            }
        }

        if (violations.length) {
            return violations;
        }
        return violations.length ? violations : undefined;
    }

    private static getChosenVariableType(entity: Owned): ExpressionType[] {
        const activity = entity as SetVariableActivity;

        // Check that the type of the variable is a valid expression type.
        if (activity && activity.variables  && activity.variables.type) {
            let variableType = activity.variables.type.toLowerCase();

            // Expressions only accept 'Bool' and variables have
            // type 'Boolean' available.
            // Likewise for 'Int' and 'Integer.
            if (variableType === 'boolean') {
                variableType = 'bool';
            } else if (variableType === 'integer') {
                variableType = 'int';
            }

            const converted = convertStringToExpressionType(variableType);

            if (converted) {
                return [converted];
            }
        }

        return undefined;
    }
}
